import { useEffect } from 'react'

import { consoleWarnCentrifuge, logCentrifuge } from '../../Centrifuge.utils'

/**
 * Attach Centrifuge connection-lifecycle log listeners to the given client
 * for the lifetime of the calling component. Detaches on unmount or when
 * `client` changes.
 *
 * Intended for use inside the value-producing `useCentrifuge` hook: a single
 * attachment per app keeps the console output to one log line per event, no
 * matter how many consumers have opted into the realtime connection. Logs
 * are dev-only via `logCentrifuge`/`consoleWarnCentrifuge`.
 *
 * @param {{
 *   client: import('centrifuge').Centrifuge | null,
 *   hasConnectedRef: Ref,
 * }} options
 */
export function useCentrifugeDebug({ client, hasConnectedRef }) {
  useEffect(() => {
    if (client) {
      function handleConnecting(ctx) {
        logCentrifuge('connecting', ctx)
      }

      function handleConnected(ctx) {
        logCentrifuge('connected', ctx)
      }

      function handleDisconnected(ctx) {
        logCentrifuge('disconnected', ctx)
      }

      function handleError(ctx) {
        if (hasConnectedRef.current) {
          consoleWarnCentrifuge('client error', ctx)
        }
      }

      client.on('connecting', handleConnecting)
      client.on('connected', handleConnected)
      client.on('disconnected', handleDisconnected)
      client.on('error', handleError)

      return () => {
        client.off('connecting', handleConnecting)
        client.off('connected', handleConnected)
        client.off('disconnected', handleDisconnected)
        client.off('error', handleError)
      }
    }
  }, [client, hasConnectedRef])
}
